import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  Home,
  Calendar,
  Sparkles,
  Users,
  User,
  CreditCard,
  Settings,
  Menu,
  LogOut,
  Target,
  Share2,
  Timer,
} from "lucide-react";
import AuthStore from "../../../stores/AuthStore";

const sidebarItems = [
  {
    titleKey: "nav.dashboard",
    icon: Home,
    href: "/dashboard",
  },
  {
    titleKey: "nav.plan",
    icon: Calendar,
    href: "/plan",
  },
  {
    titleKey: "nav.aiChat",
    icon: Sparkles,
    href: "/chat",
    featured: true, // Highlight this item
  },
  {
    titleKey: "nav.tribes",
    icon: Users,
    href: "/tribes",
  },
  {
    titleKey: "nav.habits",
    icon: Target,
    href: "/habits",
  },
  {
    titleKey: "nav.sharedInbox",
    icon: Share2,
    href: "/shared",
  },
  {
    titleKey: "nav.focus",
    icon: Timer,
    href: "/focus",
  },
  {
    titleKey: "nav.profile",
    icon: User,
    href: "/profile",
  },
  {
    titleKey: "nav.subscription",
    icon: CreditCard,
    href: "/subscription",
  },
  {
    titleKey: "nav.settings",
    icon: Settings,
    href: "/settings",
  },
];

export function Sidebar({ className }) {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();

  const currentLang = i18n.language || "en";

  const handleLanguageChange = (lang) => {
    if (lang !== currentLang) {
      i18n.changeLanguage(lang);
    }
  };

  const handleLogout = () => {
    queryClient.clear();
    AuthStore.logout();
    navigate("/");
  };

  return (
    <div
      className={cn(
        "pb-12 min-h-screen border-r bg-background flex flex-col w-64",
        className
      )}
    >
      <div className="space-y-6 py-6 flex-1 overflow-y-auto">
        <div className="px-4">
          {/* Logo */}
          <div className="mb-8 px-2 flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center shadow-lg">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <h2 className="text-2xl font-bold tracking-tight text-foreground">
              Rituely
            </h2>
          </div>

          {/* Main Navigation */}
          <div className="space-y-1.5">
            {sidebarItems.map((item) => {
              const isActive =
                location.pathname === item.href ||
                location.pathname.startsWith(item.href + "/");

              if (item.featured) {
                // Special style for AI button
                return (
                  <Link key={item.href} to={item.href}>
                    <div
                      className={cn(
                        "group relative flex items-center gap-3 rounded-xl px-3 py-3 transition-all",
                        "bg-gradient-to-r from-primary to-primary/80 text-white shadow-lg hover:shadow-xl",
                        isActive && "shadow-2xl scale-[1.02]"
                      )}
                    >
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/20">
                        <item.icon className="w-5 h-5" />
                      </div>
                      <span className="font-semibold">{t(item.titleKey)}</span>
                      {isActive && (
                        <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-white" />
                      )}
                    </div>
                  </Link>
                );
              }

              // Regular navigation items
              return (
                <Link key={item.href} to={item.href}>
                  <div
                    className={cn(
                      "group relative flex items-center gap-3 rounded-xl px-3 py-3 transition-all",
                      isActive
                        ? "bg-primary/10 text-primary font-medium"
                        : "text-muted-foreground hover:bg-accent hover:text-foreground"
                    )}
                  >
                    <div
                      className={cn(
                        "w-8 h-8 flex items-center justify-center rounded-lg transition-colors",
                        isActive
                          ? "bg-primary/20"
                          : "bg-accent/50 group-hover:bg-accent"
                      )}
                    >
                      <item.icon
                        className={cn(
                          "w-5 h-5 transition-all",
                          isActive && "stroke-[2.5]"
                        )}
                      />
                    </div>
                    <span
                      className={cn(
                        "transition-all",
                        isActive && "font-semibold"
                      )}
                    >
                      {t(item.titleKey)}
                    </span>
                    {isActive && (
                      <div className="absolute right-3 w-1.5 h-1.5 rounded-full bg-primary" />
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer: Language switcher + Logout */}
      <div className="px-4 py-4 mt-auto border-t space-y-3">
        {/* Language Switcher */}
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {t("nav.language")}
          </span>
          <div className="flex items-center gap-1">
            <Button
              variant={currentLang.startsWith("en") ? "default" : "outline"}
              size="xs"
              className="h-7 px-2 text-xs"
              onClick={() => handleLanguageChange("en")}
            >
              EN
            </Button>
            <Button
              variant={currentLang.startsWith("tr") ? "default" : "outline"}
              size="xs"
              className="h-7 px-2 text-xs"
              onClick={() => handleLanguageChange("tr")}
            >
              TR
            </Button>
          </div>
        </div>

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className={cn(
            "group w-full flex items-center gap-3 rounded-xl px-3 py-3 transition-all",
            "text-destructive hover:bg-destructive/10 hover:text-destructive/90"
          )}
        >
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-destructive/10 group-hover:bg-destructive/20">
            <LogOut className="w-5 h-5" />
          </div>
          <span className="font-medium">{t("nav.logout")}</span>
        </button>
      </div>
    </div>
  );
}

export function MobileSidebar() {
  const [open, setOpen] = React.useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" className="md:hidden">
          <Menu className="h-6 w-6" />
          <span className="sr-only">Toggle Menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="p-0 w-72">
        <Sidebar className="border-none" />
      </SheetContent>
    </Sheet>
  );
}
